import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { CampaignsTableMeta } from 'Modules/Plans/Campaigns/types';
import { CellContext } from '@tanstack/react-table';
import { CampaignTableRow } from '../../types';
import { useState } from 'react';
import { Dropdown, assets } from '@round/whitelabel-ui';
import styles from './ActionsCell.module.css';
import { usePublicReportTiktokPosts } from 'Modules/Plans/PublicReports/Posts/TiktokPosts/hooks/usePublicReportTiktokPosts';
import TiktokReportClipboardTable, {
    mapToClipboardReportRow,
    mapToClipboardReportTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/TiktokReportClipboardTable';
import copyComponentRenderToClipboard from 'utility/copyToClipboard/copyComponentRenderToClipboard';
import { showNotification } from 'helpers';

const { Copy: CopyIcon } = assets;

const getTableMeta = getTableMetaHelper<CampaignsTableMeta>();

type Props = CellContext<CampaignTableRow, any>;

const ActionsCell = ({ table, row: { original } }: Props) => {
    const { isLoading, getIsCampaignStatsLoading } = getTableMeta(table);
    const { fetchImmediate } = usePublicReportTiktokPosts(original.id, { enabled: false });
    const [isCopyLoading, setIsCopyLoading] = useState(false);

    if (isLoading || getIsCampaignStatsLoading?.(original)) {
        return <Skeleton />;
    }

    const onCopyReport = async (campaign: CampaignTableRow) => {
        try {
            setIsCopyLoading(true);
            const result = await fetchImmediate();
            if (!result) {
                showNotification('Could not copy', 'error');
                return;
            }

            const { posts, users, userStatsCache } = result;
            const items = posts.map((post) => {
                const accountId = post.tiktok_details?.account_id;
                const user = users.find((u) => u.id === accountId) ?? null;
                const userStats = userStatsCache.find((u) => u.user_id === accountId) ?? null;

                return mapToClipboardReportRow({ post, user, userStats });
            });

            const totals = mapToClipboardReportTotals(items, campaign.stats);
            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals,
                    items,
                    includeCost: posts.some((p) => 'cost' in p),
                },
                TiktokReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        } finally {
            setIsCopyLoading(false);
        }
    };

    return (
        <Dropdown>
            {({ setIsOpen }) => (
                <>
                    <Dropdown.Target />
                    <Dropdown.Menu>
                        <span className={styles.copyMenuTitle}>Copy</span>
                        <Dropdown.Item
                            icon={() => <CopyIcon className={styles.copyIcon} />}
                            onClick={() => {
                                onCopyReport(original).finally(() => {
                                    setIsOpen(false);
                                });
                            }}
                        >
                            {isCopyLoading ? 'Report loading...' : 'Report'}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </>
            )}
        </Dropdown>
    );
};

export default ActionsCell;
